// src/features/workflows/WorkflowsPage.jsx
//
// W2: CLOSURE-WF-HOME — Workflows home (runs list + KPIs + peek drawer).
//
// Reimagined landing page with:
//   - 4 KPI tiles (Active runs, Needs attention, Waiting on a team, Templates)
//   - Filterable/searchable runs list with 6 columns (Workflow, Owner,
//     Current phase, Health, Next action, Updated)
//   - Active / Completed / All tabs with live counts
//   - Board toggle (visible but disabled — placeholder only)
//   - Peek drawer (read-only side overlay; row click → peek;
//     "Open full run →" navigates to /workflows/runs/:id — ClosureRunPage)
//
// ── Field source map (GET /api/workflows/runs) ────────────────────────────────
//
// KPI tiles:
//   Active runs       → run.status === 'in_progress'
//   Needs attention   → run.health === 'Needs attention'   (computeRunHealth)
//   Waiting on a team → run.health === 'Waiting on a team' (computeRunHealth)
//   Templates         → templates.length
//
// Workflow column:
//   run.name, run.templateName, run.sourceLabel
//   Progress → run.checksDone / run.totalChecks ("N of M checks")
//             Falls back to run.phasesDone / run.phaseCount for non-closure runs
//
// Owner:         run.ownerName
// Current phase: run.currentPhaseName, run.phasesDone, run.phaseCount
// Health:        run.health (computeRunHealth — pre-computed server-side)
// Next action:   run.nextActionLabel + run.nextActionDetail (first failing/waiting check)
//               Derived server-side from evaluateClosureChecks — first non-pass check.
//               Fallback to health+phase heuristic for non-closure runs.
// Updated:       run.startedAt → run.createdAt (relative)
//
// Peek drawer:
//   conservation  → run.conservation  { peopleToMove, sourceSeats, targetAbsorbed, balanced }
//   phaseGlance   → run.phaseGlance   [{ name, done, total, issue }] — 3 closure phases
//   All read-only. "Open full run →" routes to /workflows/runs/:id via useNavigate.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, LayoutList, LayoutGrid, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { DrawerCloseButton } from "../../v2/components/DrawerCloseButton.jsx";
import { T } from "../../app/tokens.js";
import { PageHeaderStack } from "../../components/PageHeaderStack.jsx";
import { KpiTileRow } from "../../features/plan/KpiTileRow.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { GuidedLaunchFlow } from "./GuidedLaunchFlow.jsx";
import { ClosureStartDrawer } from "./ClosureStartDrawer.jsx";
import { RunDetailPage } from "./RunDetailPage.jsx";
import { NotificationsBell } from "./NotificationsBell.jsx";
import { getWorkflowRuns, getWorkflowTemplates } from "../../data/workflowsClient.js";
import { log } from "../../utils/logger.js";

// ─── Role gate ───────────────────────────────────────────────────────────────

const CAN_EDIT_ROLES = new Set(["editor", "admin"]);

// ─── Health pill metadata ─────────────────────────────────────────────────────
// Source: computeRunHealth() in server/lib/workflowFactEngine.js
// Values: "Needs attention" | "Waiting on a team" | "On track"

const HEALTH_META = {
  "Needs attention":   { bg: T.redSoft,    fg: T.red,              dot: T.red              },
  "Waiting on a team": { bg: T.ragAmberBg, fg: T.ragAmberText,     dot: T.ragAmberText     },
  "On track":          { bg: T.greenSoft,  fg: T.green,            dot: T.green            },
};

// ─── Status → tab key mapping ─────────────────────────────────────────────────

const TAB_FILTER = {
  active:    (r) => r.status === "in_progress" || r.status === "on_hold",
  completed: (r) => r.status === "completed",
  all:       ()  => true,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtRelative(d) {
  if (!d) return "—";
  const ms = Date.now() - new Date(d).getTime();
  if (Number.isNaN(ms)) return "—";
  const mins  = Math.floor(ms / 60_000);
  const hours = Math.floor(ms / 3_600_000);
  const days  = Math.floor(ms / 86_400_000);
  if (mins  < 1)   return "just now";
  if (mins  < 60)  return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days  < 30)  return `${days}d ago`;
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Derive a "next action" label + detail from the run object.
//
// Primary: use server-computed fields from evaluateClosureChecks output.
//   run.nextActionLabel  — first non-pass check's label (server-side derivation)
//   run.nextActionDetail — first non-pass check's factLabel (one-line detail)
//   Available for closure runs (those with sourceLocationId).
//
// Fallback: health + status for non-closure runs where these fields are absent.
function deriveNextAction(run) {
  if (!run) return { label: "—", detail: null };

  // Primary path — from server-computed check results (closure runs only)
  if (run.nextActionLabel) {
    return {
      label:  run.nextActionLabel,
      detail: run.nextActionDetail ?? null,
    };
  }

  // Fallback — status / health heuristic for non-closure runs
  if (run.status === "completed") {
    return { label: "Completed", detail: null };
  }
  const h     = run.health;
  const phase = run.currentPhaseName;
  if (h === "Needs attention") {
    return { label: phase ? `Resolve issues in "${phase}"` : "Resolve failing checks", detail: null };
  }
  if (h === "Waiting on a team") {
    return { label: phase ? `Awaiting external team - ${phase}` : "Waiting on external team", detail: null };
  }
  if (phase) return { label: `Advance "${phase}"`, detail: null };
  return { label: "—", detail: null };
}

// Initials from an owner name string (up to 2 chars).
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─── HealthPill ───────────────────────────────────────────────────────────────

function HealthPill({ health, testId }) {
  if (!health) return <span style={{ fontSize: T.fsXs, color: T.mid }}>—</span>;
  const meta = HEALTH_META[health] || { bg: T.bg, fg: T.mid };
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: meta.bg, color: meta.fg,
        fontSize: T.fsXs, fontWeight: T.fwSemibold,
        borderRadius: 6, padding: "2px 10px", whiteSpace: "nowrap",
      }}
    >
      {health}
    </span>
  );
}

// ─── OwnerBadge ──────────────────────────────────────────────────────────────

function OwnerBadge({ name }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <div style={{
        width: 26, height: 26, borderRadius: "50%",
        background: T.blueSoft, color: T.blue,
        fontSize: T.fsXxs, fontWeight: T.fwBold,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {initials(name)}
      </div>
      <span style={{ fontSize: T.fsSm5, color: T.dark, fontWeight: T.fwSemibold }}>
        {name || "Unassigned"}
      </span>
    </div>
  );
}

// ─── CheckProgress ───────────────────────────────────────────────────────────
// Shows "N of M checks" progress bar for closure runs (checksDone/totalChecks)
// or "N of M phases" for non-closure runs (phasesDone/phaseCount).

function CheckProgress({ run }) {
  // Closure run: use check-level counts (server-derived from evaluateClosureChecks)
  if (run.totalChecks > 0) {
    const pct = Math.round((run.checksDone / run.totalChecks) * 100);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 7 }}>
        <div style={{ height: 4, width: 90, background: T.border, borderRadius: 999, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: T.blue, borderRadius: 999 }} />
        </div>
        <span style={{ fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwBold }}>
          {run.checksDone} of {run.totalChecks} checks
        </span>
      </div>
    );
  }
  // Non-closure run: fall back to phase-level counts
  if (run.phaseCount > 0) {
    const pct = Math.round((run.phasesDone / run.phaseCount) * 100);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 7 }}>
        <div style={{ height: 4, width: 90, background: T.border, borderRadius: 999, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: T.blue, borderRadius: 999 }} />
        </div>
        <span style={{ fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwBold }}>
          {run.phasesDone} of {run.phaseCount} phases
        </span>
      </div>
    );
  }
  return null;
}

// ─── ConservationCard ─────────────────────────────────────────────────────────
// Peek drawer mini-card showing move count, source seats, and absorption balance.
// Source: run.conservation from computeConservation() — closure runs only.
// Only rendered when run.conservation is present (server best-effort).

function ConservationCard({ conservation }) {
  if (!conservation) return null;
  const { peopleToMove, sourceSeats, targetAbsorbed, balanced, unbalancedReason } = conservation;

  const verdictColor = balanced ? T.green : T.red;
  const verdictText  = balanced
    ? "Balanced"
    : `Unbalanced - ${unbalancedReason || "Workday remap outstanding"}`;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
        textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6,
      }}>
        Conservation
      </div>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
          <div style={{ padding: "10px 13px", borderRight: `1px solid ${T.border}` }}>
            <div style={{ fontSize: T.fsXxs, color: T.mid }}>People to move</div>
            <div style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, marginTop: 2 }}>
              {peopleToMove ?? "—"}
            </div>
          </div>
          <div style={{ padding: "10px 13px", borderRight: `1px solid ${T.border}` }}>
            <div style={{ fontSize: T.fsXxs, color: T.mid }}>Source seats</div>
            <div style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, marginTop: 2 }}>
              {sourceSeats ?? "—"}
            </div>
          </div>
          <div style={{ padding: "10px 13px" }}>
            <div style={{ fontSize: T.fsXxs, color: T.mid }}>Target absorbed</div>
            <div style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, marginTop: 2 }}>
              {targetAbsorbed ?? "—"}
            </div>
          </div>
        </div>
        <div style={{
          borderTop: `1px solid ${T.border}`, padding: "7px 13px",
          fontSize: T.fsXxs, fontWeight: T.fwBold, color: verdictColor,
        }}>
          {verdictText}
        </div>
      </div>
    </div>
  );
}

// ─── PhaseGlance ─────────────────────────────────────────────────────────────
// 3-phase check progress in the peek drawer.
// Source: run.phaseGlance [{ name, done, total, issue }] — closure runs only.
// Derived server-side from evaluateClosureChecks per phase slice.

function PhaseGlance({ phaseGlance }) {
  if (!phaseGlance || phaseGlance.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
        textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6,
      }}>
        Phase progress
      </div>
      <div style={{
        background: T.card, border: `1px solid ${T.border}`,
        borderRadius: 10, overflow: "hidden",
      }}>
        {phaseGlance.map((phase, idx) => {
          const allDone = phase.done === phase.total;
          const pct     = phase.total > 0 ? Math.round((phase.done / phase.total) * 100) : 0;
          const stateColor = phase.issue ? T.red
            : allDone ? T.green
            : T.blue;

          return (
            <div
              key={phase.name}
              style={{
                padding: "10px 13px",
                borderBottom: idx < phaseGlance.length - 1 ? `1px solid ${T.border}` : "none",
              }}
            >
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 5,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  {allDone
                    ? <CheckCircle2 size={13} color={T.green} />
                    : phase.issue
                      ? <AlertCircle size={13} color={T.red} />
                      : <Clock size={13} color={T.mid} />}
                  <span style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>
                    {phase.name}
                  </span>
                </div>
                <span style={{
                  fontSize: T.fsXxs, fontWeight: T.fwBold,
                  color: stateColor,
                }}>
                  {phase.done}/{phase.total} checks
                </span>
              </div>
              <div style={{ height: 3, background: T.border, borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: stateColor, borderRadius: 999 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RunsTable ───────────────────────────────────────────────────────────────
//
// 6-column layout per the W2 mockup (docs/workflow-home-comp.html).
// Selected row: background tint, no edge stripe (house rule).

const COL_WIDTHS = "minmax(260px,1.8fr) 140px 150px 130px minmax(180px,1fr) 90px";

function RunsTable({ runs, selectedId, onSelectRun }) {
  if (runs.length === 0) {
    return (
      <div
        data-testid="workflows-empty-state"
        style={{
          padding: "28px 20px", textAlign: "center",
          fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold,
          background: T.card, borderTop: `1px solid ${T.border}`,
        }}
      >
        No runs here yet. New runs appear reactively when offices change or when
        you start one manually - honestly empty until then.
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "grid", gridTemplateColumns: COL_WIDTHS, columnGap: 14,
          padding: "10px 20px",
          background: T.bg, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
          fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
          textTransform: "uppercase", letterSpacing: 0.3,
        }}
      >
        <div>Action</div>
        <div>Owner</div>
        <div>Current phase</div>
        <div>Health</div>
        <div>Next action</div>
        <div>Updated</div>
      </div>

      {runs.map((run) => {
        const isSelected = run.id === selectedId;
        const { label: nextLabel, detail: nextDetail } = deriveNextAction(run);
        const updatedTs = run.startedAt || run.createdAt;
        const dotColor  = run.health
          ? (HEALTH_META[run.health]?.dot ?? T.mid)
          : (run.status === "completed" ? T.green : T.mid);

        return (
          <button
            key={run.id}
            type="button"
            data-testid={`wf-run-row-${run.id}`}
            onClick={() => onSelectRun(run.id)}
            style={{
              width: "100%", textAlign: "left", cursor: "pointer",
              border: "none", borderBottom: `1px solid ${T.border}`,
              background: isSelected ? T.blueSoft : "transparent",
              display: "grid", gridTemplateColumns: COL_WIDTHS,
              columnGap: 14, padding: "14px 20px", alignItems: "center",
              transition: "background 0.1s",
            }}
          >
            {/* Workflow: state dot + name + template·source + progress */}
            <div style={{ display: "grid", gridTemplateColumns: "9px 1fr", gap: 10, alignItems: "start", minWidth: 0 }}>
              <div style={{
                width: 9, height: 9, borderRadius: "50%",
                background: dotColor, marginTop: 5, flexShrink: 0,
              }} />
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {run.name}
                </div>
                {/* Sub-line: template · location/market · source label */}
                <div style={{
                  fontSize: T.fsXs, color: T.mid, marginTop: 2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {[run.templateName, run.sourceLabel].filter(Boolean).join(" · ")}
                </div>
                {/* Progress bar: "N of M checks" (closure) or "N of M phases" (other) */}
                <CheckProgress run={run} />
              </div>
            </div>

            {/* Owner */}
            <OwnerBadge name={run.ownerName} />

            {/* Current phase */}
            <div>
              <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>
                {run.currentPhaseName || (run.status === "completed" ? "Done" : "—")}
              </div>
              {run.phaseCount > 0 && (
                <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>
                  Phase {Math.min(run.phasesDone + 1, run.phaseCount)} of {run.phaseCount}
                </div>
              )}
            </div>

            {/* Health — run.health from computeRunHealth */}
            <HealthPill health={run.health} testId={`wf-health-${run.id}`} />

            {/* Next action — server-derived from first non-pass check */}
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {nextLabel}
              </div>
              {nextDetail && (
                <div style={{
                  fontSize: T.fsXxs, color: T.mid, marginTop: 2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {nextDetail}
                </div>
              )}
            </div>

            {/* Updated */}
            <div style={{ fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwSemibold }}>
              {fmtRelative(updatedTs)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── PeekDrawer ──────────────────────────────────────────────────────────────
//
// Read-only right-side overlay. Populated from the same run object in the list.
// "Open full run →" uses useNavigate() → /workflows/runs/:id (ClosureRunPage).
//
// Sections:
//   Header         — eyebrow (templateName) · run name · labeled Owner + Progress chips
//   Signal block   — big RAG pill (run.health) + "Run health" label + reason line
//   ConservationCard — bordered 3-cell grid + verdict line (balanced/unbalanced)
//   PhaseGlance    — run.phaseGlance [{ name, done, total, issue }]
//   Next action    — brow-style row: label left, state string right
//   Related        — sourceLabel / targetLabel value-preview links (omitted if absent)
//   Footer         — Close (secondary) + "Open full run →" (primary), right-aligned

function PeekDrawer({ run, onClose }) {
  const navigate = useNavigate();

  function handleOpenFull() {
    onClose();
    navigate(`/workflows/runs/${run.id}`);
  }

  if (!run) return null;

  const healthMeta = HEALTH_META[run.health] || { bg: T.bg, fg: T.mid };
  const { label: nextLabel, detail: nextDetail } = deriveNextAction(run);

  // Labeled chips: check-level then phase-level progress
  const progressLabel = run.totalChecks > 0
    ? `${run.checksDone} of ${run.totalChecks} checks`
    : run.phaseCount > 0
      ? `${run.phasesDone} of ${run.phaseCount} phases`
      : null;

  // Signal block reason line — "Blocked on {label} · phase N of M"
  const healthReason = (() => {
    if (run.nextActionLabel) {
      const phaseStr = run.phaseCount > 0
        ? ` · phase ${Math.min((run.phasesDone ?? 0) + 1, run.phaseCount)} of ${run.phaseCount}`
        : "";
      return `Blocked on ${run.nextActionLabel}${phaseStr}`;
    }
    return run.health || null;
  })();

  // Related section: present when at least one of sourceLabel / targetLabel exists.
  // Sub-detail fallbacks derive from existing run fields when server-supplied detail is absent.
  const hasRelated = !!(run.sourceLabel || run.targetLabel);
  const sourceDetailLine = run.sourceDetail
    || (run.templateName ? run.templateName : null)
    || (run.currentPhaseName ? `Current phase: ${run.currentPhaseName}` : null);
  const targetDetailLine = run.targetDetail
    || (run.conservation?.targetAbsorbed != null
      ? `Absorbing ${run.conservation.targetAbsorbed} seats`
      : null);

  return (
    <>
      <div
        data-testid="peek-drawer-backdrop"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 49, background: "rgba(10,25,47,0.28)" }}
      />
      <div
        data-testid="peek-drawer"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(480px, 96vw)",
          background: T.white, borderLeft: `1px solid ${T.border}`,
          display: "flex", flexDirection: "column", zIndex: 50,
          boxShadow: "-4px 0 24px rgba(18,39,70,0.10)",
        }}
      >
        <DrawerCloseButton onClick={onClose} testId="peek-drawer-close" />
        {/* Header: eyebrow → run name → labeled chips */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${T.border}` }}>
          <div>
            <div style={{ minWidth: 0, paddingRight: 36 }}>
              {/* Eyebrow — template name in all-caps */}
              {run.templateName && (
                <div style={{
                  fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
                  textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4,
                }}>
                  {run.templateName}
                </div>
              )}
              {/* Run name */}
              <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, lineHeight: 1.3 }}>
                {run.name}
              </div>
              {/* Labeled chips row */}
              <div style={{ display: "flex", gap: 14, marginTop: 10, flexWrap: "wrap" }}>
                {run.ownerName && (
                  <div>
                    <div style={{
                      fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
                      textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3,
                    }}>
                      Owner
                    </div>
                    <span
                      data-testid="peek-owner-chip"
                      style={{
                        display: "inline-flex", alignItems: "center",
                        background: T.bg, color: T.dark,
                        fontSize: T.fsXs, fontWeight: T.fwSemibold,
                        borderRadius: 6, padding: "2px 10px",
                        border: `1px solid ${T.border}`,
                      }}
                    >
                      {run.ownerName}
                    </span>
                  </div>
                )}
                {progressLabel && (
                  <div>
                    <div style={{
                      fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
                      textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3,
                    }}>
                      Progress
                    </div>
                    <span
                      data-testid="peek-progress-chip"
                      style={{
                        display: "inline-flex", alignItems: "center",
                        background: T.bg, color: T.mid,
                        fontSize: T.fsXs, fontWeight: T.fwSemibold,
                        borderRadius: 6, padding: "2px 10px",
                      }}
                    >
                      {progressLabel}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>

          {/* Signal block — big RAG pill + "Run health" label + reason */}
          {run.health && (
            <div
              data-testid="peek-health-chip"
              style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}
            >
              <span style={{
                display: "inline-flex", alignItems: "center",
                background: healthMeta.bg, color: healthMeta.fg,
                fontSize: T.fsSm5, fontWeight: T.fwBold,
                borderRadius: 9, padding: "6px 11px",
                whiteSpace: "nowrap", flexShrink: 0,
              }}>
                {run.health}
              </span>
              <div>
                <div style={{
                  fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
                  textTransform: "uppercase", letterSpacing: 0.4,
                }}>
                  Run health
                </div>
                {healthReason && (
                  <div style={{ fontSize: T.fsXs, color: T.dark, marginTop: 2 }}>
                    {healthReason}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conservation bordered grid — run.conservation from computeConservation() */}
          <ConservationCard conservation={run.conservation} />

          {/* Phase progress — run.phaseGlance from evaluateClosureChecks per phase */}
          <PhaseGlance phaseGlance={run.phaseGlance} />

          {/* Next action — brow-style row */}
          <div style={{
            fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
            textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6,
          }}>
            Next action
          </div>
          <div
            data-testid="peek-next-action"
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 0", borderTop: `1px solid ${T.border}`,
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>
              {nextLabel}
            </div>
            {nextDetail && (
              <div style={{
                fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark,
                marginLeft: 12, flexShrink: 0,
              }}>
                {nextDetail}
              </div>
            )}
          </div>

          {/* Related section — source office + target office value-preview links */}
          {hasRelated && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
                textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6,
              }}>
                Related
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {run.sourceLabel && (
                  <div style={{
                    border: `1px solid ${T.border}`, borderRadius: 9,
                    padding: "10px 13px",
                  }}>
                    <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.blue }}>
                      {run.sourceLabel}
                    </div>
                    {sourceDetailLine && (
                      <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>
                        {sourceDetailLine}
                      </div>
                    )}
                  </div>
                )}
                {run.targetLabel && (
                  <div style={{
                    border: `1px solid ${T.border}`, borderRadius: 9,
                    padding: "10px 13px",
                  }}>
                    <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.blue }}>
                      {run.targetLabel}
                    </div>
                    {targetDetailLine && (
                      <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 3 }}>
                        {targetDetailLine}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer — right-aligned, no "Peek · quick look" label */}
        <div style={{
          padding: "14px 20px", borderTop: `1px solid ${T.border}`,
          display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8,
        }}>
          <button
            type="button"
            data-testid="peek-close-btn"
            onClick={onClose}
            style={{
              height: 36, padding: "0 14px", borderRadius: 9,
              border: `1px solid ${T.border}`, background: T.card,
              color: T.mid, fontWeight: T.fwSemibold, fontSize: T.fsSm5, cursor: "pointer",
            }}
          >
            Close
          </button>
          <button
            type="button"
            data-testid="peek-open-full-btn"
            onClick={handleOpenFull}
            style={{
              height: 36, padding: "0 14px", borderRadius: 9,
              border: "none", background: T.blue,
              color: T.white, fontWeight: T.fwBold, fontSize: T.fsSm5, cursor: "pointer",
            }}
          >
            Open full run →
          </button>
        </div>
      </div>
    </>
  );
}

// ─── WorkflowsHomeView ───────────────────────────────────────────────────────

function WorkflowsHomeView({ runs, templates, loading, error, canEdit, onStartWorkflow }) {
  const [tab,    setTab]    = useState("active");
  const [search, setSearch] = useState("");
  const [peekId, setPeekId] = useState(null);

  // KPI counts — all from live run/template data
  const kpis = useMemo(() => ({
    activeRuns:     runs.filter((r) => r.status === "in_progress").length,
    needsAttention: runs.filter((r) => r.health === "Needs attention").length,
    waitingOnTeam:  runs.filter((r) => r.health === "Waiting on a team").length,
    templates:      templates.length,
  }), [runs, templates]);

  // Tab counts
  const tabCounts = useMemo(() => ({
    active:    runs.filter(TAB_FILTER.active).length,
    completed: runs.filter(TAB_FILTER.completed).length,
    all:       runs.length,
  }), [runs]);

  // Filtered list
  const filtered = useMemo(() => {
    const base = runs.filter(TAB_FILTER[tab] ?? (() => true));
    const q = search.trim();
    if (!q) return base;
    return base.filter((r) =>
      matchesFoldedQuery(q, r.name, r.templateName, r.ownerName, r.sourceLabel)
    );
  }, [runs, tab, search]);

  const peekRun = useMemo(() => runs.find((r) => r.id === peekId) ?? null, [runs, peekId]);

  function handleRowClick(id) {
    setPeekId((prev) => (prev === id ? null : id));
  }

  const TABS = [
    { key: "active",    label: "Active"    },
    { key: "completed", label: "Completed" },
    { key: "all",       label: "All"       },
  ];

  return (
    <div data-testid="workflows-home-view">
      {/* KPI tiles */}
      <KpiTileRow testId="workflows-kpi-tiles">
        <KpiTile testId="workflows-kpi-active"          label="Active runs"          rawValue={kpis.activeRuns}     tileIndex={0} color={T.blue}  />
        <KpiTile testId="workflows-kpi-needs-attention" label="Needs attention"       rawValue={kpis.needsAttention} tileIndex={1} color={kpis.needsAttention > 0 ? T.red : undefined} />
        <KpiTile testId="workflows-kpi-waiting"         label="Waiting on a team"    rawValue={kpis.waitingOnTeam}  tileIndex={2} color={kpis.waitingOnTeam > 0 ? T.negotiateOrange : undefined} />
        <KpiTile testId="workflows-kpi-templates"       label="Templates"            rawValue={kpis.templates}      tileIndex={3} />
      </KpiTileRow>

      {/* Panel */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: 16, overflow: "hidden", marginTop: 2,
      }}>
        {/* Panel header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px 13px", borderBottom: `1px solid ${T.border}`,
        }}>
          <div>
            <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 3 }}>
              Actions in motion
            </div>
            <div style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwSemibold }}>
              Ownership, phase, health, and the next action - one operational list.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
            {/* List/Board toggle — Board is visible-but-disabled placeholder */}
            <div style={{
              display: "flex", background: T.bg, border: `1px solid ${T.border}`,
              borderRadius: 10, padding: 3,
            }}>
              <button
                type="button"
                data-testid="workflows-view-list"
                style={{
                  border: "none", borderRadius: 7, padding: "6px 11px",
                  background: T.card, color: T.blue,
                  boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
                  cursor: "default", display: "flex", alignItems: "center", gap: 5,
                  fontSize: T.fsXs, fontWeight: T.fwBold,
                }}
              >
                <LayoutList size={13} /> List
              </button>
              <button
                type="button"
                data-testid="workflows-view-board"
                disabled
                title="Board view coming soon"
                style={{
                  border: "none", borderRadius: 7, padding: "6px 11px",
                  background: "transparent", color: T.mid,
                  cursor: "not-allowed", display: "flex", alignItems: "center", gap: 5,
                  fontSize: T.fsXs, fontWeight: T.fwBold, opacity: 0.55,
                }}
              >
                <LayoutGrid size={13} /> Board
              </button>
            </div>
            {/* + Start workflow */}
            {canEdit && (
              <button
                type="button"
                data-testid="workflows-start-launch"
                onClick={onStartWorkflow}
                style={{
                  height: 38, padding: "0 15px", borderRadius: 10,
                  border: "none", background: T.blue, color: T.white,
                  fontWeight: T.fwBold, fontSize: T.fsSm5, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                }}
              >
                <Plus size={14} /> Start action
              </button>
            )}
          </div>
        </div>

        {/* Tabs + search */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "12px 20px", borderBottom: `1px solid ${T.border}`,
          flexWrap: "wrap",
        }}>
          {TABS.map(({ key, label }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`workflows-tab-${key}`}
                onClick={() => { setTab(key); setPeekId(null); }}
                style={{
                  border: "none", borderRadius: 8, padding: "6px 11px",
                  background: active ? T.blueSoft : "transparent",
                  color: active ? T.blue : T.mid,
                  fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer",
                }}
              >
                {label} <span style={{ opacity: 0.8 }}>{tabCounts[key]}</span>
              </button>
            );
          })}
          <div style={{ marginLeft: "auto", position: "relative", minWidth: 220 }}>
            <Search
              size={13}
              color={T.mid}
              style={{
                position: "absolute", left: 10, top: "50%",
                transform: "translateY(-50%)", pointerEvents: "none",
              }}
            />
            <input
              data-testid="workflows-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPeekId(null); }}
              placeholder="Search action, office, owner…"
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "7px 10px 7px 30px",
                border: `1px solid ${T.border}`, borderRadius: 9,
                fontSize: T.fsXs, background: T.white, color: T.dark,
              }}
            />
          </div>
        </div>

        {/* Runs table */}
        {loading ? (
          <div style={{ padding: 20, fontSize: T.fsSm, color: T.mid }}>Loading runs…</div>
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBanner error={error} /></div>
        ) : (
          <RunsTable runs={filtered} selectedId={peekId} onSelectRun={handleRowClick} />
        )}
      </div>

      {/* Peek drawer */}
      {peekRun && (
        <PeekDrawer run={peekRun} onClose={() => setPeekId(null)} />
      )}
    </div>
  );
}

// ─── WorkflowsPage ───────────────────────────────────────────────────────────

export function WorkflowsPage({
  currentRole,
  currentUserId = null,
  launchSource = null,
  onLaunchSourceConsumed,
  openRunId = null,
  onOpenRunIdConsumed,
  onNavigateToSource,
}) {
  const canEdit = CAN_EDIT_ROLES.has(currentRole);

  const [screen,             setScreen]             = useState(launchSource ? "launch" : openRunId ? "detail" : "list");
  const [selectedRunId,      setSelectedRunId]      = useState(openRunId || null);
  const [activeLaunchSource, setActiveLaunchSource] = useState(launchSource);
  const [showClosureDrawer,  setShowClosureDrawer]  = useState(false);

  useEffect(() => {
    if (launchSource) {
      setActiveLaunchSource(launchSource);
      setScreen("launch");
      onLaunchSourceConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchSource]);

  useEffect(() => {
    if (openRunId) {
      setSelectedRunId(openRunId);
      setScreen("detail");
      onOpenRunIdConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRunId]);

  const [runs,        setRuns]        = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError,   setRunsError]   = useState(null);
  const [templates,   setTemplates]   = useState([]);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    setRunsError(null);
    try {
      const list = await getWorkflowRuns();
      setRuns(list);
    } catch (err) {
      log.error("WorkflowsPage: failed to load runs", err);
      setRunsError(err);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const list = await getWorkflowTemplates();
      setTemplates(list);
    } catch (err) {
      log.error("WorkflowsPage: failed to load templates", err);
    }
  }, []);

  useEffect(() => {
    if (screen === "list") {
      loadRuns();
      loadTemplates();
    }
  }, [screen, loadRuns, loadTemplates]);

  function handleOpenRun(id) {
    setSelectedRunId(id);
    setScreen("detail");
  }

  function handleLaunched(runId) {
    setSelectedRunId(runId);
    setScreen("detail");
  }

  function handleBackToList() {
    setScreen("list");
    setSelectedRunId(null);
    setActiveLaunchSource(null);
  }

  const screenTitle = screen === "launch" ? "Start an action" : "Run detail";
  const screenDesc  = screen === "launch"
    ? "Follow the steps to launch a new action run."
    : "Track progress, advance phases, and review activity.";

  return (
    <div data-testid="workflows-page">
      <PageHeaderStack>
        <div style={{
          display: "flex",
          justifyContent: screen === "list" ? "flex-end" : "space-between",
          alignItems: "flex-start", gap: 12,
          marginBottom: screen === "list" ? 0 : 4,
        }}>
          {screen !== "list" && (
            <div>
              <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark }}>{screenTitle}</div>
              <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>{screenDesc}</div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <NotificationsBell onOpenRun={handleOpenRun} />
          </div>
        </div>

        {screen === "launch" && (
          <GuidedLaunchFlow onCancel={handleBackToList} onLaunched={handleLaunched} source={activeLaunchSource} />
        )}

        {screen === "detail" && selectedRunId && (
          <RunDetailPage
            runId={selectedRunId}
            canEdit={canEdit}
            currentRole={currentRole}
            currentUserId={currentUserId}
            onBack={handleBackToList}
            onNavigateToSource={onNavigateToSource}
          />
        )}

        {screen === "list" && (
          <WorkflowsHomeView
            runs={runs}
            templates={templates}
            loading={runsLoading}
            error={runsError}
            canEdit={canEdit}
            onStartWorkflow={() => setShowClosureDrawer(true)}
          />
        )}

        {/* Closure start drawer — rendered as overlay above list view */}
        {showClosureDrawer && (
          <ClosureStartDrawer
            canEdit={canEdit}
            onClose={() => setShowClosureDrawer(false)}
            onLaunched={(runId) => {
              setShowClosureDrawer(false);
              handleLaunched(runId);
            }}
          />
        )}
      </PageHeaderStack>
    </div>
  );
}
